import { ParticipantType } from '@entities/participants';
import type { UserInfo } from '@entities/user';
import type {
  BusAgentListPayload,
  BusAgentStatusListPayload,
  TeammateAvailabilityChangePayload,
} from '@main/event-listeners/payloads/bus-agent-list.payload';
import type { BusTeamListPayload } from '@main/event-listeners/payloads/bus-team-list.payload';
import type {
  BusDialerCallStatusPayload,
  BusDialerSyncPayload,
  BusIncomingCallPayload,
} from '@main/event-listeners/payloads/bus-incoming-call.payload';
import type { BusUserAvailabilityChangedPayload } from '@main/event-listeners/payloads/bus-user-availability-changed.payload';
import type { IntegrationsettingUpdateReceivedPayload } from '@main/event-listeners/payloads/integration-setting-update-received.payload';
import type { BusRegistrationFulfilled } from '@main/bus-layer/bus-registration';
import { ControllerFactory } from '@main/factories/controller.factory';
import { FeatureFlagStore } from '@main/sf-connector/feature-flag-store';
import type { Logger } from '@main/utils/logger';
import { PhoneConnector } from '@main/sf-connector/scv-telephony-connector';
import { ScvObjectManager } from '@main/sf-connector/scv-object-manager';

export type BusDialerEvent =
  | 'DIALER.INCOMING'
  | 'DIALER.SYNC'
  | 'DIALER.CALL_STATUS';

export type BusUserAvailabilityChangedEvent = 'USER.AVAILABILITY_CHANGED';
export type IntegrationSettingUpdateEvent =
  'SUBSCRIBE.INTEGRATION_SETTING_UPDATE_RECEIVED';
export type BusRegistrationEvent =
  | 'BUS.REGISTER_FULFILLED'
  | 'API.FETCH_CURRENT_USER_FULFILLED';

export interface BusEventData<T = unknown> {
  type:
    | BusDialerEvent
    | BusUserAvailabilityChangedEvent
    | IntegrationSettingUpdateEvent
    | BusRegistrationEvent;
  payload: T;
}

export type BusEventMap = {
  'DIALER.INCOMING': (payload: BusIncomingCallPayload) => Promise<unknown>;
  'DIALER.SYNC': (payload: BusDialerSyncPayload) => Promise<unknown>;
  'DIALER.CALL_STATUS': (
    payload: BusDialerCallStatusPayload,
  ) => Promise<unknown>;
  'USER.AVAILABILITY_CHANGED': (
    payload: BusUserAvailabilityChangedPayload,
  ) => Promise<unknown>;
  'API.LOOKUP_TEAMMATES_FULFILLED': (
    payload: BusAgentListPayload,
  ) => Promise<unknown>;
  'API.FETCH_AVAILABILITIES_FULFILLED': (
    payload: BusAgentStatusListPayload,
  ) => Promise<unknown>;
  'API.SEARCH_TEAMS_FULFILLED': (
    payload: BusTeamListPayload,
  ) => Promise<unknown>;
  'USER.TEAMMATE_AVAILABILITY_CHANGED': (
    payload: TeammateAvailabilityChangePayload,
  ) => Promise<unknown>;
  'SUBSCRIBE.INTEGRATION_SETTING_UPDATE_RECEIVED': (
    payload: IntegrationsettingUpdateReceivedPayload,
  ) => Promise<unknown>;
  'BUS.REGISTER_FULFILLED': (
    payload: BusRegistrationFulfilled | undefined,
  ) => Promise<unknown>;
  'API.FETCH_CURRENT_USER_FULFILLED': (payload: UserInfo) => Promise<unknown>;
};

export class BusEventListener {
  private eventMap: BusEventMap;
  // Tracks if voicemail options were hidden due to transfer
  private voicemailOptionsHidden = false;
  // Set when mandatoryTagging handles the disconnect so the subsequent
  // 'ended' and 'idle' events don't fire a second HANGUP / removeCalls()
  // that would clear an already-arriving second call from the repository.
  private mandatoryTaggingDisconnectHandled = false;

  constructor(private logger: Logger) {
    this.eventMap = {
      'DIALER.INCOMING': this.incomingCallEventHandler,
      'DIALER.SYNC': this.dialerSyncEventHandler,
      'DIALER.CALL_STATUS': this.dialerCallStatusEventHandler,
      'USER.AVAILABILITY_CHANGED': this.userAvailabilityChangedEventHandler,
      'API.LOOKUP_TEAMMATES_FULFILLED': this.storeAgentListEventHandler,
      'API.FETCH_AVAILABILITIES_FULFILLED': this.syncAgentStatusEventHandler,
      'API.SEARCH_TEAMS_FULFILLED': this.storeTeamListEventHandler,
      'USER.TEAMMATE_AVAILABILITY_CHANGED':
        this.userTeammateAvailabilityChangedEventHandler,
      'SUBSCRIBE.INTEGRATION_SETTING_UPDATE_RECEIVED':
        this.integrationSettingUpdateReceivedEventHandler,
      'BUS.REGISTER_FULFILLED': this.busRegisterFulfilledEventHandler,
      'API.FETCH_CURRENT_USER_FULFILLED':
        this.fetchCurrentUserFulfilledEventHandler,
    };
  }

  private busRegisterFulfilledEventHandler = async (
    payload: BusRegistrationFulfilled | undefined,
  ) => {
    if (payload?.currentUser?.company_id) {
      FeatureFlagStore.init(payload.currentUser.company_id);
    }
  };

  private fetchCurrentUserFulfilledEventHandler = async (payload: UserInfo) => {
    if (payload?.company_id) {
      FeatureFlagStore.init(payload.company_id);
    }
  };

  private userTeammateAvailabilityChangedEventHandler = async (
    payload: TeammateAvailabilityChangePayload,
  ) => {
    const controller =
      ControllerFactory.buildTeammateAvailabilityChangedController();
    return controller.handle(payload);
  };
  private storeAgentListEventHandler = async (payload: BusAgentListPayload) => {
    const controller = ControllerFactory.buildStoreAgentListController(); // get data
    return controller.handle(payload);
  };

  private syncAgentStatusEventHandler = async (
    payload: BusAgentStatusListPayload,
  ) => {
    const controller = ControllerFactory.buildSyncAgentStatusController(); // get data
    return controller.handle(payload);
  };

  private storeTeamListEventHandler = async (payload: BusTeamListPayload) => {
    const controller = ControllerFactory.buildStoreTeamListController(); // get data
    return controller.handle(payload);
  };

  private userAvailabilityChangedEventHandler = async (
    payload: BusUserAvailabilityChangedPayload,
  ) => {
    const controller =
      ControllerFactory.buildStatusSyncAircallToCrmController();
    return controller.handle(payload);
  };

  private integrationSettingUpdateReceivedEventHandler = async (
    payload: IntegrationsettingUpdateReceivedPayload,
  ) => {
    const controller =
      ControllerFactory.buildSyncIntegrationSettingUpdateController();
    return controller.handle(payload);
  };

  private incomingCallEventHandler = async (
    payload: BusIncomingCallPayload,
  ) => {
    const controller = ControllerFactory.buildRingIncomingCallController();
    return controller.handle(payload);
  };

  private dialerSyncEventHandler = async (payload: BusDialerSyncPayload) => {
    const controller = ControllerFactory.buildSyncParticipantsController();
    return controller.handle({
      ...payload.metadata,
      callId: payload.metadata.callUuid,
    });
  };

  private dialerCallStatusEventHandler = async (
    payload: BusDialerCallStatusPayload,
  ) => {
    const { status } = payload;
    if (payload.flow === 'warmTransfer') {
      const prerecordedMessageController =
        ControllerFactory.buildFetchPrerecordedMessagesController();

      prerecordedMessageController.handleHideComponent();
      this.voicemailOptionsHidden = true;
      if (status === 'connected') {
        const controller =
          ControllerFactory.buildConnectedTransferCallController();
        const { callUuid, to, from } = payload;

        if (callUuid) {
          return controller.handle({
            callUuid,
            from,
            to,
          });
        } else {
          return controller.handle();
        }
      }
      if (status === 'ended' || status === 'hanging') {
        const controller =
          ControllerFactory.buildDisconnectedTransferCallController();
        return controller.handle();
      }
    } else if (payload.flow === 'regular') {
      if (status === 'connected') {
        if (!this.voicemailOptionsHidden) {
          const prerecordedMessageController =
            ControllerFactory.buildFetchPrerecordedMessagesController();
          prerecordedMessageController.handle().catch(console.error);
        }
        const controller = ControllerFactory.buildConnectedCallController();
        const transferCallController =
          ControllerFactory.buildDisconnectedTransferCallController();
        const holdcontroller = ControllerFactory.buildHoldToggleController();
        holdcontroller.handle({
          onHold: false,
          participantType: ParticipantType.EXTERNAL,
        });
        const { callUuid, to, from } = payload;
        transferCallController.handle();
        if (callUuid) {
          return controller.handle({
            callUuid,
            from,
            to,
          });
        } else {
          return controller.handle();
        }
      }
      if (status === 'ended') {
        this.voicemailOptionsHidden = false;
        if (this.mandatoryTaggingDisconnectHandled) {
          // The disconnect was already handled when 'mandatoryTagging' fired.
          // This 'ended' arrives after the agent submits tags (DIALER.RESET →
          // DIALER.CALL_STATUS ended). Running DisconnectedCallController here
          // would call removeCalls() a second time and could clear a second
          // incoming call that arrived while the agent was tagging.
          console.log(
            '[BusEventListener] skipping ended: disconnect already handled at mandatoryTagging',
          );
          return;
        }
        // Tag fetch is Salesforce-initiated on call end (the widget sends
        // SCV.FETCH_TAGS); here we just run the normal disconnected-call flow.
        const controller = ControllerFactory.buildDisconnectedCallController();
        return controller.handle();
      }
      if (status === 'mandatoryTagging') {
        // The dialer holds the call in mandatory-tagging until tags are set via
        // DIALER.SET_TAGS. Fetch the tags for the screen and tell the widget
        // tagging is required so it blocks completion until a tag is applied.
        this.voicemailOptionsHidden = false;
        this.mandatoryTaggingDisconnectHandled = true;
        ScvObjectManager.fetchTags().catch(console.error);
        ScvObjectManager.notifyScv({
          type: 'SCV.MANDATORY_TAGGING_REQUIRED',
          data: { callId: payload.callUuid },
        });
        // The caller has already disconnected; end the Salesforce telephony
        // session immediately so Omni-Channel reflects the ended call and the
        // agent's capacity is freed without waiting for tag submission.
        const disconnectedController =
          ControllerFactory.buildDisconnectedCallController();
        await disconnectedController.handle();
        return;
      }
      if (status === 'hanging') {
        this.voicemailOptionsHidden = false;
      }
    }
    if (status === 'idle') {
      await new PhoneConnector().removeSupervisor();
      if (this.mandatoryTaggingDisconnectHandled) {
        // The mandatory-tagging disconnect was already handled. The 'idle' event
        // here is the dialer finalizing call1's lifecycle. Running
        // IdleCallController would find call2 in the repository (if it arrived
        // while the agent was tagging) and fire HANGUP + removeCalls() on it.
        this.mandatoryTaggingDisconnectHandled = false;
        console.log(
          '[BusEventListener] skipping idle disconnect: already handled at mandatoryTagging',
        );
        return;
      }
      const controller = ControllerFactory.buildIdleCallController();
      return controller.handle();
    }
  };

  private eventDispatcher = async (event: MessageEvent<BusEventData>) => {
    const {
      data: { type, payload },
    } = event;

    console.log('eventDispatcher', { type, payload });

    const handler = this.eventMap[type];

    if (handler) {
      const loggerMessage = `Action: Bus event listener | Event: ${type}`;
      try {
        const response = await handler(<never>payload);
        if (response instanceof Error) {
          this.logger.error(loggerMessage, {
            payload,
            response,
          });
        } else if (!!response) {
          this.logger.info(loggerMessage, {
            payload,
            response,
          });
        }
      } catch (error) {
        this.logger.error(loggerMessage, {
          payload,
          error,
        });
      }
    }
  };

  addEventListener() {
    window.addEventListener('message', this.eventDispatcher);
  }
}
